import { useState } from "react";
import { FileText, Zap } from "@semo/icons";
import { cn, Button } from "@semo/shared-ui";
import type { ChatRole } from "../types";
import type { ContractDocumentOptionDto } from "@semo/api-contracts";

/**
 * SEMO 자체 견적서로 대체 발급하는 c-market paperCode.
 * (backend `cmarket/domain/types/contract-document.types.ts` 의 SEMO_QUOTE_PAPER_CODE 와 일치)
 * c-market 응답이 autoGenerated=false 로 오더라도 SEMO 가 항상 자동 발급한다.
 */
const SEMO_QUOTE_PAPER_CODE = 1;

interface ChatDocumentRequestCardProps {
  /** 선택 가능한 계약서류 목록 (c-market 응답 스냅샷) */
  documents: ContractDocumentOptionDto[];
  /** 현재 사용자 역할 - 구매자(buyer)만 선택/요청 가능 */
  role: ChatRole;
  /** 요청 상태 */
  status?: "pending" | "requested";
  /** 이미 처리된(자동 발급 or 직접 제출 요청 완료) paperCode 목록 */
  issuedPaperCodes?: number[];
  /**
   * [계약서류 요청] 버튼 핸들러
   *
   * 자동 생성 서류는 즉시 채팅창에 첨부되고, 직접 제출 서류는 판매자에게 요청 메시지가 전송된다.
   */
  onRequestDocuments?: (
    paperCodes: number[],
    idempotencyKey: string,
  ) => Promise<void>;
}

/**
 * 계약서류 선택 카드 (c-market 연동)
 *
 * 계약 체결 직후 시스템이 buyer에게만 발송하는 카드 (supplier에게는 노출되지 않음).
 * UI 는 견적 등록 [추가 조건] 리스트와 동일한 pill 버튼 그룹.
 * - 자동 발급 가능 서류: 우측에 Zap 아이콘 노출
 * - 3 가지 상태 색상: default(회색) / active(파랑) / 요청 완료(초록)
 */
export function DocumentRequestCard({
  documents,
  role,
  status = "pending",
  issuedPaperCodes = [],
  onRequestDocuments,
}: ChatDocumentRequestCardProps) {
  const isBuyer = role === "buyer";

  // 견적서는 c-market 응답과 무관하게 SEMO 가 항상 자동 발급하므로 자동 생성 항목으로 취급.
  // autoGenerated 단일 필드로 판정 (기존 supported 필드는 제거됨).
  const isAutoGeneratable = (doc: ContractDocumentOptionDto) =>
    doc.paperCode === SEMO_QUOTE_PAPER_CODE || doc.autoGenerated === true;

  // 아직 처리되지 않은 서류는 자동 생성/직접 제출 상관없이 buyer가 선택 가능
  const isSelectable = (doc: ContractDocumentOptionDto) =>
    !issuedPaperCodes.includes(doc.paperCode);

  // 카드는 항상 모두 미선택 상태로 시작한다 — buyer 가 필요한 서류만 명시적으로 고르도록.
  // (종전 isDefault 프리셀렉트는 제거)
  const [selectedCodes, setSelectedCodes] = useState<number[]>([]);
  const [isRequesting, setIsRequesting] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState(() =>
    crypto.randomUUID(),
  );

  const toggleDocument = (paperCode: number) => {
    if (!isBuyer) return;
    setSelectedCodes((prev) =>
      prev.includes(paperCode)
        ? prev.filter((code) => code !== paperCode)
        : [...prev, paperCode],
    );
  };

  const hasUnprocessedDocuments = documents.some((doc) => isSelectable(doc));
  const isRequestEnabled =
    isBuyer && hasUnprocessedDocuments && selectedCodes.length > 0;

  const handleRequest = async () => {
    if (!isRequestEnabled || !onRequestDocuments) return;
    setIsRequesting(true);
    try {
      await onRequestDocuments(selectedCodes, idempotencyKey);
      setSelectedCodes([]);
      setIdempotencyKey(crypto.randomUUID());
    } finally {
      setIsRequesting(false);
    }
  };

  return (
    <div className="w-full flex flex-col bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
      {/* Header */}
      <div className="px-4 py-3 bg-primary/5 flex items-center gap-2">
        <FileText className="w-4 h-4 text-primary-text" />
        <span className="text-sm font-semibold text-primary-text">
          원하시는 계약서류가 있으신가요?
        </span>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        <p className="text-xs text-slate-500 leading-snug">
          필요한 서류를 선택해 주세요. 자동 생성이 가능한 서류(
          <Zap className="inline w-3 h-3 text-primary-text -mt-0.5" />)는 즉시
          제공되고, 직접 제출이 필요한 서류는 판매자에게 요청됩니다. (다중
          선택 가능)
        </p>

        {/* Pill Button Group — 견적 등록 [추가 조건] 스타일과 동일 */}
        <div className="flex flex-wrap gap-2" role="group">
          {documents.map((doc) => {
            const selectable = isSelectable(doc);
            const active = selectedCodes.includes(doc.paperCode);
            const isIssued = issuedPaperCodes.includes(doc.paperCode);
            const isAuto = isAutoGeneratable(doc);
            const disabled = !selectable || !isBuyer;

            return (
              <button
                key={doc.paperCode}
                type="button"
                role="checkbox"
                aria-checked={active || isIssued}
                aria-label={doc.name}
                disabled={disabled}
                onClick={() => toggleDocument(doc.paperCode)}
                className={cn(
                  // Base pill
                  "inline-flex items-center gap-1.5 h-9 px-4 rounded-full border font-medium text-sm",
                  "transition-all duration-200 select-none",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60",
                  // State: 요청 완료
                  isIssued &&
                    "bg-emerald-50 text-emerald-700 border-emerald-200 cursor-not-allowed",
                  // State: 선택 중 (active)
                  !isIssued &&
                    active &&
                    "bg-primary text-primary-foreground border-primary shadow-md shadow-primary/20",
                  // State: default (미선택 & 미완료)
                  !isIssued &&
                    !active &&
                    selectable &&
                    "bg-white text-slate-700 border-slate-200 hover:border-primary/40 hover:bg-primary/5",
                  // Supplier(읽기 전용) / 기타 disabled
                  !isIssued &&
                    !active &&
                    !selectable &&
                    "bg-slate-50 text-slate-400 border-slate-100 cursor-not-allowed",
                )}
              >
                <span className="break-keep">{doc.name}</span>
                {isAuto && (
                  <Zap
                    className={cn(
                      "w-3.5 h-3.5 shrink-0",
                      isIssued
                        ? "text-emerald-600"
                        : active
                          ? "text-white"
                          : "text-primary-text",
                    )}
                    aria-hidden="true"
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Action Area (구매자 전용) */}
      {isBuyer && (
        <div className="px-4 pb-4 space-y-1.5">
          <Button
            onClick={() => {
              void handleRequest();
            }}
            disabled={!isRequestEnabled || isRequesting}
            className="w-full py-2.5 h-auto text-sm font-semibold rounded-xl"
          >
            {isRequesting ? "요청 처리 중..." : "계약서류 요청"}
            {selectedCodes.length > 0 && ` (${String(selectedCodes.length)})`}
          </Button>
          {!isRequestEnabled && (
            <p className="text-2xs text-slate-400 text-center">
              {!hasUnprocessedDocuments
                ? "모든 계약서류가 처리되었습니다."
                : status === "requested"
                  ? "추가로 요청할 서류를 선택해 주세요."
                  : "요청할 서류를 1개 이상 선택해 주세요."}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
